/* eslint-disable react-hooks/exhaustive-deps */
import { checkEnableParseJSON, formatDate, setArrayLength } from 'components/helper/helper';
import React from 'react';
import ItemContent, { CheckboxContent } from './ItemContent';
import { Typography } from '@mui/material';
import { Button } from 'react-bootstrap';
import { USER_ROLE } from 'constants/common';
import { ModalCheckFamilyField } from 'screen/family/common/ModalCheckMissingField';
import { getUserRole } from 'utils/sessionStorage';

interface Props {
  dataValues: any;
  isParentNo2?: boolean;
}

// Helper function to format boolean values
const formatBoolean = (value: any): string => {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return value || '-';
};

const ParentSum = (props: Props) => {
  const dataValues = {
    ...props.dataValues,
    csu_info: checkEnableParseJSON(props.dataValues?.csu_info),
    parent_graduation_college_array: checkEnableParseJSON(
      props.dataValues?.parent_graduation_college_array
    ),
  };

  const [institutions, setInstitutions] = React.useState<any>();
  const [show, setShow] = React.useState(false);

  const handleClose = () => setShow(false);

  React.useEffect(() => {
    const result = (dataValues?.parent_graduation_college_array ?? []).map(
      (e: any, index: number) => {
        if (!e) return '';
        const degreeList = setArrayLength(
          [
            e?.degreesReceived1 + ' ' + e?.graduationYear1,
            e?.degreesReceived2 + ' ' + e?.graduationYear2,
            e?.degreesReceived3 + ' ' + e?.graduationYear3,
            e?.degreesReceived4 + ' ' + e?.graduationYear4,
            e?.degreesReceived5 + ' ' + e?.graduationYear5,
          ],
          e?.degreesNumber
        );
        return {
          isNew: e?.school?.id ? false : true,
          name: `${e?.school?.name} - ${degreeList.join(',')} `,
        };
      }
    );
    if (result.length) {
      setInstitutions(result);
    }
  }, [props.dataValues]);

  return (
    <div className="gm-summary-container">
      <div className="d-flex justify-content-between mb-4">
        <Typography sx={{ fontSize: '1.75rem', fontWeight: '500', color: '#4E5A6F' }}>
          {props.isParentNo2 ? 'Parent 2' : 'Parent 1'}
        </Typography>

        {getUserRole() === USER_ROLE.student && (
          <Button variant="primary" className="p-2 px-3" onClick={() => setShow(!show)}>
            Check missing values
          </Button>
        )}
      </div>

      {/* Living Status Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Living Status
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">
              {props.isParentNo2 ? 'Is "Parent No.2" living?' : 'Is "Parent No.1" living?*'}
            </div>
            <div className="gm-summary-field-value">
              {formatBoolean(props.isParentNo2 ? dataValues?.is_parent_2_living : dataValues?.is_parent_1_living)}
            </div>
          </div>
          {(props.isParentNo2 ? dataValues?.is_parent_2_living : dataValues?.is_parent_1_living) === 'false' && (
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Date deceased*</div>
              <div className="gm-summary-field-value">
                {dataValues?.date_deceased || '-'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Relationship Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Relationship
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Type*</div>
            <div className="gm-summary-field-value">
              {dataValues?.relationship_type || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Relationship to you*</div>
            <div className="gm-summary-field-value">
              {dataValues?.relationship_to_you || '-'}
            </div>
          </div>
        </div>
        {dataValues?.relationship_to_you === 'Other' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Other*</div>
              <div className="gm-summary-field-value">
                {dataValues?.other_relationship_to_you_free_text || '-'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Basic & Contact Information Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Basic & Contact Information
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Prefix</div>
            <div className="gm-summary-field-value">
              {dataValues?.prefix || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">First/Given name*</div>
            <div className="gm-summary-field-value">
              {dataValues?.first_name || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Middle Initial</div>
            <div className="gm-summary-field-value">
              {dataValues?.middle_name || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Last Name*</div>
            <div className="gm-summary-field-value">
              {dataValues?.last_name || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Former Last Name</div>
            <div className="gm-summary-field-value">
              {dataValues?.former_last_name || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Suffix</div>
            <div className="gm-summary-field-value">
              {dataValues?.suffix || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Birth country/region*</div>
            <div className="gm-summary-field-value">
              {dataValues?.birth_country || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Email address*</div>
            <div className="gm-summary-field-value">
              {dataValues?.prefered_email || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Country Code*</div>
            <div className="gm-summary-field-value">
              {dataValues?.country_code || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Phone Number</div>
            <div className="gm-summary-field-value">
              {dataValues?.phone_number || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Phone Number Type</div>
            <div className="gm-summary-field-value">
              {dataValues?.phone_number_type || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Parent Address is*</div>
            <div className="gm-summary-field-value">
              {dataValues?.parent_address_is || '-'}
            </div>
          </div>
        </div>

        {dataValues?.parent_address_is === 'A different address' && (
          <div className="gm-summary-conditional-section">
            <div className="gm-summary-conditional-title">
              Different Address Information
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Country*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.different_parent_address_country || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Address Line 1*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.different_parent_address_line_1 || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Address Line 2</div>
                <div className="gm-summary-field-value">
                  {dataValues?.different_parent_address_line_2 || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">City*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.different_parent_address_city || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              {dataValues?.different_parent_address_country === 'United States of America' ? (
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label">State*</div>
                  <div className="gm-summary-field-value">
                    {dataValues?.different_parent_address_state || '-'}
                  </div>
                </div>
              ) : (
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label">Province*</div>
                  <div className="gm-summary-field-value">
                    {dataValues?.different_parent_address_province || '-'}
                  </div>
                </div>
              )}
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Zip Code*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.different_parent_address_zip_code || '-'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Authorization Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Authorization
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Do you give permission for the admission office to communicate with your Parent/Guardian regarding your application?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.is_give_permission_communicate_to)}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Do you give permission for your parent/guardian to receive university communications? These may include new student and housing information, parent activities, volunteer and charitable opportunities, and general campus information.*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.is_give_permission_to_receive_information)}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">I authorize the use of this parent/guardian information to be used as my emergency contact when I enroll at a CSU.</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.authorize_parent_guardian_as_emergency_contact)}
            </div>
          </div>
        </div>
        <div className="gm-summary-checkbox-container">
          <div className="gm-summary-text-primary">
            <span
              style={{
                marginRight: '12px',
                fontSize: '18px',
                color: formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? '#1890ff' : '#d9d9d9',
                fontWeight: formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? 'bold' : 'normal',
              }}
            >
              {formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? '✓' : '○'}
            </span>
            I authorize the University of California to share my parent's contact information.
          </div>
        </div>
      </div>

      {/* Employment Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Employment
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">What is this parent's current job status?*</div>
            <div className="gm-summary-field-value">
              {dataValues?.parent_current_job_status || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Employment status*</div>
            <div className="gm-summary-field-value">
              {dataValues?.employment_status || '-'}
            </div>
          </div>
        </div>

        {dataValues?.parent_current_job_status === 'Stay-at-home parent/homemaker' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">How many years has this parent been a stay-at-home parent?</div>
              <div className="gm-summary-field-value">
                {dataValues?.number_of_year_at_home || '-'}
              </div>
            </div>
          </div>
        )}
        {dataValues?.parent_current_job_status === 'Not-employed' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">How many years has this parent not been employed?</div>
              <div className="gm-summary-field-value">
                {dataValues?.number_of_year_not_employed || '-'}
              </div>
            </div>
          </div>
        )}
        {dataValues?.parent_current_job_status === 'Retired' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">How many years has this parent been retired?</div>
              <div className="gm-summary-field-value">
                {dataValues?.number_of_year_retired || '-'}
              </div>
            </div>
          </div>
        )}

        {dataValues?.parent_current_job_status === 'Employed' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Current Employer</div>
              <div className="gm-summary-field-value">
                {dataValues?.current_employer || '-'}
              </div>
            </div>
          </div>
        )}

        <div className="gm-summary-field-label" style={{ marginBottom: '16px', marginTop: '16px' }}>
          Most recent/Former job held by this parent info:
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Occupation (former occupation, if retired or deceased)</div>
            <div className="gm-summary-field-value">
              {dataValues?.occupation || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Job Category*</div>
            <div className="gm-summary-field-value">
              {dataValues?.job_category || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Job title*</div>
            <div className="gm-summary-field-value">
              {dataValues?.job_title || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Number of years at job</div>
            <div className="gm-summary-field-value">
              {dataValues?.number_of_year_at_job || '-'}
            </div>
          </div>
        </div>

        {['Employed', 'Retired'].includes(dataValues?.employment_status) && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Is parent currently employed by or retired from a college or university?*</div>
              <div className="gm-summary-field-value">
                {formatBoolean(dataValues?.parent_currently_employed_or_retire_from_college)}
              </div>
            </div>
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">College/University employer lookup*</div>
              <div className="gm-summary-field-value" style={{ 
                color: checkEnableParseJSON(dataValues?.college_or_university)?.id ? '#4d88f0' : 'black' 
              }}>
                {checkEnableParseJSON(dataValues?.college_or_university)
                  ? checkEnableParseJSON(dataValues?.college_or_university)?.name
                  : dataValues?.college_or_university || '-'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Education Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Education
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Education Level*</div>
            <div className="gm-summary-field-value">
              {dataValues?.education_level || '-'}
            </div>
          </div>
          {institutions && (
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Total number of institutions attended*</div>
              <div className="gm-summary-field-value">
                {institutions?.length || '-'}
              </div>
            </div>
          )}
        </div>
        {institutions && institutions.length > 0 && (
          <div className="gm-summary-conditional-section">
            <div className="gm-summary-conditional-title">
              Institutions attended and degrees received
            </div>
            {institutions.map((e: any, index: number) => (
              <div key={index} className="gm-summary-field-grid">
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label"></div>
                  <div className="gm-summary-field-value" style={{ 
                    color: e.isNew ? 'black' : '#4d88f0' 
                  }}>
                    {e.name || '-'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Residency Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Residency
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Is your parent/guardian a current permanent resident of California?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia)}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Has your parent/guardian lived in California continuously since birth?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.isLivedInCaliforniaContinuouslySinceBirth)}
            </div>
          </div>
        </div>

        {/* {dataValues?.csu_info?.isLivedInCaliforniaContinuouslySinceBirth !== 'false' &&
         dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia !== 'false' && ( */}
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Prior Country</div>
              <div className="gm-summary-field-value">
                {dataValues?.csu_info?.priorCountry || '-'}
              </div>
            </div>
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Prior State/Territory</div>
              <div className="gm-summary-field-value">
                {dataValues?.csu_info?.priorState || '-'}
              </div>
            </div>
          </div>
        {/* )} */}

        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">From when to when did he/she live there</div>
            <div className="gm-summary-field-value">
              {formatDate(dataValues?.csu_info?.fromWhenToWhenLiveThere) || '-'}
            </div>
          </div>
          {dataValues?.csu_info?.isLivedInCaliforniaContinuouslySinceBirth !== 'false' && (
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">To</div>
              <div className="gm-summary-field-value">
                {formatDate(dataValues?.csu_info?.ToWhenToWhenLiveThere) || '-'}
              </div>
            </div>
          )}
        </div>

        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">When did his/her current stay in California begin?*</div>
            <div className="gm-summary-field-value">
              {formatDate(dataValues?.csu_info?.whenStayInCaliforniaBegin) || '-'}
            </div>
          </div>
        </div>

        {dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia === 'false' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Which U.S. State/possession does your parent/guardian regard as her permanent home?</div>
              <div className="gm-summary-field-value">
                {dataValues?.csu_info?.stateRegardAsPermanentHome || '-'}
              </div>
            </div>
          </div>
        )}
      </div>

      {getUserRole() === USER_ROLE.student && (
        <ModalCheckFamilyField
          show={show}
          onClose={handleClose}
          data={{
            ...dataValues,
            college_or_university: checkEnableParseJSON(dataValues?.college_or_university),
          }}
          page={
            props.isParentNo2 ? 'parent-2' : 'parent-1'
          }
        />
      )}
    </div>
  );
};

export default ParentSum;
